import { jsxs, jsx } from 'react/jsx-runtime';
import { createContext, useContext, useCallback, useState, Fragment, createElement } from 'react';

const generateUniqueId = (prefix = "") => {
  const timestamp = Date.now().toString(36);
  const randomValue = Math.random().toString(36).substring(2);
  return `${prefix}${timestamp}${randomValue}`;
};
const renderComponentStack = (componentStack) => /* @__PURE__ */ jsx(Fragment, { children: componentStack.map(([id, DialogComponent, props]) => /* @__PURE__ */ createElement(DialogComponent, { ...props, key: id })) });
const context = createContext({});
const useShowDialog = () => {
  const { addComponent, removeComponent } = useContext(context);
  if (!addComponent || !removeComponent) {
    throw new Error(
      "The useShowDialog hook must be used from within a <ShowDialogProvider> component"
    );
  }
  const showDialog = useCallback(
    (DialogComponent, onModalResult, props) => {
      const id = generateUniqueId("dialog");
      const requiredProps = {
        open: true,
        onModalResult: (mr) => {
          removeComponent(id);
          onModalResult(mr);
        }
      };
      const allProps = {
        ...props,
        ...requiredProps
      };
      addComponent(id, DialogComponent, allProps);
    },
    [addComponent, removeComponent]
  );
  return showDialog;
};
const ShowDialogProvider = ({ children }) => {
  const [componentStack, setComponentStack] = useState([]);
  const addComponent = (id, component, props) => {
    setComponentStack((stack) => [...stack, [id, component, props]]);
  };
  const removeComponent = (id) => {
    setComponentStack((stack) => [...stack.filter((item) => item[0] !== id)]);
  };
  return /* @__PURE__ */ jsxs(context.Provider, { value: { addComponent, removeComponent }, children: [
    renderComponentStack(componentStack),
    children
  ] });
};

export { ShowDialogProvider, useShowDialog };
